<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from "vue";
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from "@heroicons/vue/24/outline";
import type { Toast } from "../composables/useToast";

const props = defineProps<{ toast: Toast | null }>();
const emit = defineEmits<{ dismiss: [id: string] }>();

const visible = ref(false);
let timer: ReturnType<typeof setTimeout> | null = null;

watch(
  () => props.toast,
  (toast) => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
    if (toast) {
      visible.value = true;
      timer = setTimeout(() => {
        visible.value = false;
        emit("dismiss", toast.id);
      }, toast.duration);
    } else {
      visible.value = false;
    }
  },
  { immediate: true },
);

const icon = computed(() => {
  switch (props.toast?.type) {
    case "success":
      return CheckCircleIcon;
    case "error":
      return ExclamationCircleIcon;
    case "warning":
      return ExclamationTriangleIcon;
    default:
      return InformationCircleIcon;
  }
});

const styles = computed(() => {
  switch (props.toast?.type) {
    case "success":
      return "bg-success-bg border-success-border text-success";
    case "error":
      return "bg-error-bg border-error-border text-error";
    case "warning":
      return "bg-warning-bg border-warning-border text-warning";
    default:
      return "bg-info-bg border-info-border text-info";
  }
});

function close() {
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
  }
  visible.value = false;
  if (props.toast) emit("dismiss", props.toast.id);
}

// Clear any in-flight timer if the component unmounts mid-toast. The
// timer captures `props.toast` and emits `dismiss` when it fires;
// Vue ignores emit-after-unmount today, but a future refactor that
// moves side effects into the parent's dismiss handler would otherwise
// run them against a dead tree.
onBeforeUnmount(() => {
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
  }
});
</script>

<template>
  <Transition
    enter-active-class="transition ease-out duration-200"
    enter-from-class="opacity-0 translate-y-2"
    enter-to-class="opacity-100 translate-y-0"
    leave-active-class="transition ease-in duration-150"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div
      v-if="visible && toast"
      data-testid="toast-notification"
      :class="[
        'fixed bottom-4 right-4 z-50 flex items-start gap-3 rounded-lg border px-4 py-3 shadow-lg min-w-[280px] max-w-[420px]',
        styles,
      ]"
      role="status"
      aria-live="polite"
    >
      <component :is="icon" class="h-5 w-5 shrink-0 mt-0.5" aria-hidden="true" />
      <p data-testid="toast-message" class="flex-1 text-sm">{{ toast.message }}</p>
      <button
        type="button"
        data-testid="toast-dismiss"
        class="shrink-0 rounded p-0.5 hover:bg-black/10 transition-colors"
        aria-label="Dismiss notification"
        @click="close"
      >
        <XMarkIcon class="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  </Transition>
</template>
